import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import {
  pointsApi,
  type ExternalAppRef,
  type PointDetailOut
} from '@/api/points';
import { emitQrEvent } from '@/api/qrAnalytics';
import { isAvailable } from '@/lib/connectorState';
import { friendlyMessage } from '@/lib/errors';
import { formatBRL } from '@/lib/format';
import { getOrCreateQrSessionId } from '@/lib/qrSession';
import { isNativeApp } from '@/lib/nativeGoogle';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  bowAndroidIntentUrl,
  isAndroidChromium,
  isAndroidPlatform,
  isApplePlatform
} from '@/lib/storeLinks';
import { useAuthStore } from '@/store/auth';
import { Button } from '@/components/ui/Button';
import { ConnectorSelectCard } from '@/components/points/ConnectorSelectCard';
import { isChargerSelectable } from '@/components/points/ChargerStatusBadge';
import { Display } from '@/components/ui/Typography';

const SUPPORT_WHATSAPP_URL =
  import.meta.env.VITE_SUPPORT_WHATSAPP_URL ?? 'https://wa.me/5527988231783';

const REFRESH_INTERVAL_MS = 10_000;

/** Borda arco-íris da marca (mesma sequência de pétalas do símbolo Bow). */
const RAINBOW_BORDER =
  'linear-gradient(100deg,#EE263E,#DE7C00,#FFB92A,#98D801,#00C072,#00BBDE,#0090E2,#835DD0,#8345BA,#C04CAE)';

/** Símbolo Bow (colorido) servido pelo Caddy em /logo/. Usado como ícone solto
 *  (loading/erro). No card da landing usamos o lockup horizontal abaixo. */
const BOW_LOGO_SRC = '/logo/bow-primario.svg';

/** Lockup horizontal oficial (símbolo + wordmark "bow"), 2150×761. */
const BOW_LOGO_HORIZONTAL_SRC = '/logo/bow-horizontal.png';

/**
 * Landing pública /qr/:stationId — destino do QR físico colado no carregador.
 *
 * Pública (sem RequireAuth), sem AppLayout. A estação Bow é multiaplicativo:
 * aceita o app Bow (operadora) + apps de roaming (Tupi, Spott…). Esta página
 * web abre antes de entrar em qualquer app — o motorista escolhe por qual quer
 * pagar, com Bow em destaque honesto pelos benefícios (tarifa, Pix, cashback).
 *
 * Desde 2026-07 o CTA da Bow empurra o APP NATIVO, em paridade com os tiles
 * dos parceiros: Android Chromium via intent:// (abre o app no conector se
 * instalado ≥1.2; senão cai na Play), iOS via App Store (scheme-attempt fica
 * pro flip pós-adoção da 1.2), desktop segue no PWA. O escape "ou continue no
 * navegador" preserva o fluxo web — deslogado vai pra /login com `state.from`.
 *
 * Visual: redesign "V1 · Canon" (Claude Design, mai/2026).
 */
export default function QRLanding() {
  const { stationId } = useParams();
  const [searchParams] = useSearchParams();
  const qrVersion = searchParams.get('v');
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const [point, setPoint] = useState<PointDetailOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Passo 1 — conector escolhido (connector_number). null = ainda não escolhido.
  // Com 1 conector só, pré-selecionamos (efeito abaixo). connector_selected é
  // emitido 1× por scan (dedup do backend).
  const [selectedConnector, setSelectedConnector] = useState<number | null>(null);
  const connectorSelectedEmittedRef = useRef(false);

  // Refs pra instrumentação do funil — qr_session_id, viewStartedAt
  // (pra time_on_page_ms), e mountedAt em performance.now() (latency_render).
  const qrSessionIdRef = useRef<string | null>(null);
  const viewStartedAtRef = useRef<number>(0);
  const performanceMountRef = useRef<number>(0);
  const availableSeenEmittedRef = useRef(false);
  // M2 bounce: marca true quando user clica CTA / outro app, suprimindo o
  // landing_left no pagehide subsequente. landingLeftEmittedRef garante
  // emit único (pagehide pode disparar 2x em alguns browsers).
  const userClickedRef = useRef(false);
  const landingLeftEmittedRef = useRef(false);

  useEffect(() => {
    if (!stationId) {
      setError('QR inválido');
      return;
    }
    qrSessionIdRef.current = getOrCreateQrSessionId();
    viewStartedAtRef.current = Date.now();
    performanceMountRef.current =
      typeof performance !== 'undefined' ? performance.now() : 0;

    // Etapa 1 do funil — view (na primeira renderização).
    emitQrEvent({
      qrSessionId: qrSessionIdRef.current,
      hubStationId: stationId,
      qrVersion,
      eventType: 'view'
    });

    let cancelled = false;
    setError(null);
    setPoint(null);

    pointsApi
      .getByHubId(stationId)
      .then((p) => {
        if (cancelled) return;
        setPoint(p);
        // Etapa 2 — landing_loaded (com latência). Emitido logo após o
        // payload chegar; React renderiza no próximo tick.
        const latency =
          performance.now() - performanceMountRef.current;
        emitQrEvent({
          qrSessionId: qrSessionIdRef.current!,
          hubStationId: stationId,
          qrVersion,
          eventType: 'landing_loaded',
          context: { latency_render_ms: Math.round(latency) }
        });

        // Etapa 3 — available_seen (1x por scan). Conta conectores no
        // payload; se 0 disponíveis, NÃO emite (a etapa fica vazia no
        // funil pra esse session, refletindo a realidade).
        const total = p.connectors.length;
        const available = p.connectors.filter((c) =>
          isAvailable(c.state)
        ).length;
        if (available > 0 && !availableSeenEmittedRef.current) {
          availableSeenEmittedRef.current = true;
          emitQrEvent({
            qrSessionId: qrSessionIdRef.current!,
            hubStationId: stationId,
            qrVersion,
            eventType: 'available_seen',
            context: {
              connectors_available_count: available,
              connectors_total_count: total
            }
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const isNotFound = err instanceof ApiError && err.status === 404;
        if (isNotFound) {
          setError('Carregador não reconhecido');
        } else {
          setError(friendlyMessage(err, 'Falha ao carregar'));
        }
        emitQrEvent({
          qrSessionId: qrSessionIdRef.current!,
          hubStationId: stationId,
          qrVersion,
          eventType: 'error',
          context: {
            error_code: isNotFound ? 'POINT_NOT_FOUND' : 'NETWORK_ERROR'
          }
        });
      });

    return () => {
      cancelled = true;
    };
  }, [stationId, qrVersion]);

  // M2 bounce — emite `landing_left` quando o usuário fecha a aba ou
  // troca de app sem clicar em "Carregar agora" nem "Já uso outro app".
  // Usa pagehide (mobile-friendly, sobrevive a unload) + visibilitychange
  // (cobre swipe-to-home no iOS Safari que nem sempre dispara pagehide).
  useEffect(() => {
    if (!stationId) return;

    const emitLandingLeft = () => {
      if (
        userClickedRef.current ||
        landingLeftEmittedRef.current ||
        !qrSessionIdRef.current
      ) {
        return;
      }
      landingLeftEmittedRef.current = true;
      const timeOnPage = Date.now() - viewStartedAtRef.current;
      emitQrEvent({
        qrSessionId: qrSessionIdRef.current,
        hubStationId: stationId,
        qrVersion,
        eventType: 'landing_left',
        context: { time_on_page_ms: timeOnPage }
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        emitLandingLeft();
      } else if (document.visibilityState === 'visible') {
        // Usuário voltou ao tab/app — atualiza imediatamente sem esperar polling
        pointsApi.getByHubId(stationId)
          .then((p) => setPoint(p))
          .catch(() => {});
      }
    };

    window.addEventListener('pagehide', emitLandingLeft);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('pagehide', emitLandingLeft);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [stationId, qrVersion]);

  // Pré-seleciona quando há um único conector E ele está selecionável — evita
  // deixar um cabo ocupado pré-marcado e o usuário travado.
  useEffect(() => {
    if (
      point &&
      point.connectors.length === 1 &&
      isChargerSelectable(point.connectors[0].state)
    ) {
      setSelectedConnector(point.connectors[0].connector_number);
    }
  }, [point]);

  // Polling a cada 10s pra manter o status dos conectores sempre fresco.
  // O Hub pode demorar 30–120s pra propagar mudança de estado OCPP após a
  // sessão encerrar — sem polling, status desatualizado ("Aguardando plug")
  // persiste até o usuário fechar e reabrir a página.
  useEffect(() => {
    if (!stationId) return;
    const id = setInterval(async () => {
      if (document.hidden) return;
      try {
        const p = await pointsApi.getByHubId(stationId);
        setPoint(p);
        if (!availableSeenEmittedRef.current && qrSessionIdRef.current) {
          const avail = p.connectors.filter((c) => isAvailable(c.state)).length;
          if (avail > 0) {
            availableSeenEmittedRef.current = true;
            emitQrEvent({
              qrSessionId: qrSessionIdRef.current,
              hubStationId: stationId,
              qrVersion,
              eventType: 'available_seen',
              context: {
                connectors_available_count: avail,
                connectors_total_count: p.connectors.length
              }
            });
          }
        }
      } catch {
        // silencioso — erro de background não interrompe o fluxo do usuário
      }
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [stationId, qrVersion]);

  if (error) {
    return <ErrorScreen message={error} onRetry={() => navigate(0)} />;
  }
  if (!point || !stationId) {
    return <LoadingScreen />;
  }

  const totalConnectors = point.connectors.length;
  const availableConnectors = point.connectors.filter((c) => isAvailable(c.state));
  const allBusy = availableConnectors.length === 0;
  const externalApps = point.external_apps;
  const multiApp = externalApps.length > 0;
  // Tiles de parceiro ordenados por tarifa asc (sem tarifa no fim) — intenção
  // do design V6: deixar explícito que a Bow é a mais barata. Sort estável
  // preserva a ordem do backend nos empates.
  const sortedApps = [...externalApps].sort((a, b) => {
    const ta = a.tariff_per_kwh_brl != null ? Number(a.tariff_per_kwh_brl) : Infinity;
    const tb = b.tariff_per_kwh_brl != null ? Number(b.tariff_per_kwh_brl) : Infinity;
    return ta - tb;
  });
  const outOfService = !point.active || totalConnectors === 0;
  // Passo 2 só fica ativo depois de escolher um conector (Passo 1). Com 1
  // conector só, o efeito de pré-seleção já deixa pronto.
  const step2Ready = selectedConnector != null;
  // CTA da Bow por plataforma (2026-07): fora do app o botão empurra o app
  // nativo. href externo só existe em mobile web com conector escolhido; nos
  // demais casos (nativo/desktop) o botão mantém o navigate interno.
  const ctaTarget = resolveBowCta();
  const ctaExternalHref =
    stationId != null && selectedConnector != null
      ? ctaTarget === 'intent_android'
        ? bowAndroidIntentUrl(stationId, selectedConnector)
        : ctaTarget === 'store_play'
          ? PLAY_STORE_URL
          : ctaTarget === 'store_apple'
            ? APP_STORE_URL
            : null
      : null;

  function onConnectorSelect(connectorNumber: number): void {
    // Guard: cabo em uso/indisponível não é selecionável (o card já bloqueia
    // via prop disabled, mas mantemos a guarda por segurança).
    const target = point?.connectors.find(
      (c) => c.connector_number === connectorNumber
    );
    if (target && !isChargerSelectable(target.state)) return;

    setSelectedConnector(connectorNumber);
    if (
      !stationId ||
      !qrSessionIdRef.current ||
      connectorSelectedEmittedRef.current
    ) {
      return;
    }
    connectorSelectedEmittedRef.current = true;
    emitQrEvent({
      qrSessionId: qrSessionIdRef.current,
      hubStationId: stationId,
      qrVersion,
      eventType: 'connector_selected',
      context: { connector_number: connectorNumber }
    });
  }

  /** Analytics do CTA principal (qualquer destino) + supressão do bounce.
   *  O emitQrEvent usa sendBeacon — sobrevive à navegação externa. */
  function trackCtaClick(target: BowCtaTarget | 'pwa_fallback'): void {
    if (!stationId || !qrSessionIdRef.current || selectedConnector == null) {
      return;
    }
    userClickedRef.current = true;
    const timeOnPage = Date.now() - viewStartedAtRef.current;
    emitQrEvent({
      qrSessionId: qrSessionIdRef.current,
      hubStationId: stationId,
      qrVersion,
      eventType: token ? 'cta_click_logged_in' : 'cta_click',
      context: {
        time_on_page_ms: timeOnPage,
        connector_number: selectedConnector,
        cta_target: target
      }
    });
  }

  /** Fluxo PWA (navegação interna) — dentro do app nativo, no desktop e no
   *  escape "ou continue no navegador". */
  function onChargeClick(
    target: 'native_in_app' | 'pwa_desktop' | 'pwa_fallback'
  ): void {
    if (!point || !stationId || !qrSessionIdRef.current) return;
    // Conector é obrigatório agora (Passo 1) — sem auto-pick. A UI bloqueia o
    // botão até a escolha, mas guardamos aqui por segurança.
    if (selectedConnector == null) return;
    const path = `/qr/${stationId}/${selectedConnector}`;

    trackCtaClick(target);

    if (token) {
      navigate(path);
    } else {
      navigate('/login', { state: { from: { pathname: path } } });
    }
  }

  function onAppClick(app: ExternalAppRef): void {
    if (!stationId || !qrSessionIdRef.current) return;
    userClickedRef.current = true;
    const timeOnPage = Date.now() - viewStartedAtRef.current;
    emitQrEvent({
      qrSessionId: qrSessionIdRef.current,
      hubStationId: stationId,
      qrVersion,
      eventType: 'other_app_click',
      context: {
        external_app_id: app.id,
        time_on_page_ms: timeOnPage,
        ...(selectedConnector != null
          ? { connector_number: selectedConnector }
          : {})
      }
    });
  }

  return (
    <div className="mx-auto flex h-[100dvh] max-w-[420px] flex-col overflow-hidden bg-bow-cream px-5 pb-2 pt-3.5 [@media(max-height:740px)]:pt-2">
      {outOfService ? (
        <div className="rounded-[24px] bg-white p-5 shadow-soft">
          <p className="rounded-2xl bg-bow-yellow/10 p-3 text-sm leading-relaxed">
            Este carregador está temporariamente fora de operação. Tente um
            ponto próximo ou fale com o suporte.
          </p>
        </div>
      ) : (
        <>
          {/* Passo 1 — selecione o conector. Cabos em uso ficam não-selecionáveis.
              Cards V5 em variante compacta (design V6 · uma página · A · contorno):
              ícone em destaque + badge-pílula de status. */}
          <section className="flex flex-col">
            <StepHeader n={1} title="Selecione o conector" />
            <div className="mt-2.5 grid grid-cols-2 gap-2.5">
              {point.connectors.map((c) => (
                <ConnectorSelectCard
                  key={c.id}
                  compact
                  connector={c}
                  selected={c.connector_number === selectedConnector}
                  disabled={!isChargerSelectable(c.state)}
                  onClick={() => onConnectorSelect(c.connector_number)}
                  letterLabel={point.capabilities?.['connector_letters'] === true}
                />
              ))}
            </div>
            {allBusy && (
              <p className="mt-2 text-center text-xs text-bow-royal/55">
                Todos os cabos estão em uso. Aguarde encerrar.
              </p>
            )}
          </section>

          {/* Passo 2 — card Bow em destaque. Bloqueado até o Passo 1 (só
              relevante com >1 conector). */}
          <section className="flex flex-col">
            <div className="mb-2 mt-[11px]">
              <StepHeader
                n={2}
                title={multiApp ? 'Pague com' : 'Carregar com a Bow'}
                hint={!step2Ready ? 'Escolha um conector acima' : undefined}
              />
            </div>
            <div
              className={`transition ${
                step2Ready ? '' : 'pointer-events-none select-none opacity-45'
              }`}
              aria-disabled={!step2Ready}
            >
              {/* Card Bow — operadora, recomendado, borda arco-íris da marca. */}
              <div
                className="rounded-[22px] shadow-mid"
                style={{ background: RAINBOW_BORDER, padding: 2 }}
              >
                <div className="rounded-[20px] bg-white px-4 py-3 [@media(max-height:740px)]:py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <img
                      src={BOW_LOGO_HORIZONTAL_SRC}
                      alt="Bow"
                      className="w-auto"
                      style={{ height: 32 }}
                    />
                    <span className="rounded-full bg-bow-royal px-2 py-[5px] text-[9px] font-bold uppercase tracking-[0.08em] text-bow-cream">
                      Recomendado
                    </span>
                  </div>

                  <div className="mt-[7px] flex flex-wrap gap-1.5">
                    <BenefitPill icon={<TagGlyph />}>Tarifa exclusiva</BenefitPill>
                    <BenefitPill icon={<PixGlyph />}>Pix</BenefitPill>
                  </div>

                  <BowTariffHero tariff={point.tariff_per_kwh_brl} />
                  {ctaExternalHref ? (
                    <>
                      {/* Fora do app: âncora empurra o app/loja. O gesto do
                          clique viaja na âncora (padrão do PartnerTile); sem
                          target=_blank — o fallback do intent:// resolve na
                          mesma aba. */}
                      <a
                        href={ctaExternalHref}
                        onClick={() => trackCtaClick(ctaTarget)}
                        className="block no-underline"
                      >
                        <Button
                          variant="primary"
                          size="lg"
                          type="button"
                          className="mt-3.5"
                        >
                          Carregar agora <Arrow />
                        </Button>
                      </a>
                      <button
                        type="button"
                        onClick={() => onChargeClick('pwa_fallback')}
                        className="mt-2 w-full text-center text-[12px] font-medium text-bow-royal/55 underline underline-offset-2 [@media(max-height:740px)]:mt-1 [@media(max-height:740px)]:text-[11px]"
                      >
                        ou continue no navegador
                      </button>
                    </>
                  ) : (
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() =>
                        onChargeClick(
                          ctaTarget === 'native_in_app'
                            ? 'native_in_app'
                            : 'pwa_desktop'
                        )
                      }
                      className="mt-3.5"
                    >
                      Carregar agora <Arrow />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      <div className="flex-1" />

      {/* Parceiros — tiles pequenos no rodapé (logo + tarifa), abrem direto
          no carregador (deep link) e caem na loja se não instalados. Mesmo
          gating do Passo 2. */}
      {!outOfService && multiApp && (
        <div
          className={`pt-2 transition ${
            step2Ready ? '' : 'pointer-events-none select-none opacity-45'
          }`}
          aria-disabled={!step2Ready}
        >
          <div className="mb-[5px] text-center text-[10px] font-semibold uppercase tracking-[0.06em] text-bow-royal/45">
            ou abra em outro app
          </div>
          <div className="flex gap-2">
            {sortedApps.map((app) => (
              <PartnerTile
                key={app.id}
                app={app}
                connector={selectedConnector}
                onClick={onAppClick}
              />
            ))}
          </div>
        </div>
      )}

      {/* Suporte */}
      <a
        href={SUPPORT_WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-2 flex items-center justify-between rounded-2xl bg-white px-3.5 py-2.5 no-underline shadow-soft [@media(max-height:740px)]:py-2"
      >
        <div className="flex items-center gap-3">
          <WhatsAppGlyph />
          <div className="flex flex-col">
            <span
              className="text-bow-royal"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: 14,
                letterSpacing: '-0.01em'
              }}
            >
              Precisa de ajuda?
            </span>
            <span className="text-[12.5px] text-bow-royal/50">
              Suporte por WhatsApp
            </span>
          </div>
        </div>
        <ChevronRight />
      </a>
    </div>
  );
}

/** Pílula de benefício do card Bow (escala compacta do design V6). */
function BenefitPill({
  children,
  icon
}: {
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-bow-royal/[0.06] px-2.5 py-[5px] text-[11px] font-semibold tracking-tight text-bow-royal">
      {icon}
      {children}
    </span>
  );
}

/** Hero de tarifa do card Bow: preço como principal vantagem, cashback ao lado.
 *  Fonte em classes Tailwind (não inline) com sub-spans em `em` pra
 *  compressão responsiva escalar tudo junto em telas baixas. */
function BowTariffHero({ tariff }: { tariff: string | number | null | undefined }) {
  return (
    <div className="mt-3.5 border-t border-bow-royal/[0.08] pt-[18px] [@media(max-height:740px)]:mt-2.5 [@media(max-height:740px)]:pt-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-bow-royal/50">
            Tarifa
          </span>
          {tariff ? (
            <div
              className="mt-1 flex items-baseline gap-1 text-[48px] leading-none text-bow-royal [@media(max-height:740px)]:text-[42px]"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 600,
                letterSpacing: '-0.035em'
              }}
            >
              <span className="text-[0.45em] font-medium opacity-55">R$</span>
              <span>{Number(tariff).toFixed(2).replace('.', ',')}</span>
              <span className="ml-0.5 text-[0.28em] font-medium opacity-55">
                /kWh
              </span>
            </div>
          ) : (
            <div className="mt-2 text-sm text-bow-royal/55">Sem cadastro</div>
          )}
          <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium text-bow-royal/60">
            <span
              className="h-[5px] w-[5px] rounded-full"
              style={{ background: '#00C072', boxShadow: '0 0 0 2px rgba(0,192,114,0.18)' }}
            />
            exclusivo no app Bow
          </div>
        </div>
        <CashbackStamp />
      </div>
    </div>
  );
}

/** Selo genérico porque o cashback varia por cliente (escala 105px do design
 *  V6; encolhe em telas baixas pra garantir uma página sem scroll). */
function CashbackStamp() {
  return (
    <div
      className="relative flex h-[105px] w-[105px] flex-shrink-0 rotate-[-8deg] flex-col items-center justify-center rounded-full border-[3px] text-[#00A664] [@media(max-height:740px)]:h-[76px] [@media(max-height:740px)]:w-[76px]"
      style={{
        borderColor: '#00A664',
        background: 'rgba(0,192,114,0.06)'
      }}
      aria-label="Cashback variável"
    >
      <span className="absolute inset-[6px] rounded-full border border-dashed border-current opacity-50" />
      <span
        className="text-[42px] leading-none [@media(max-height:740px)]:text-[33px]"
        style={{
          fontFamily: 'Chillax, system-ui, sans-serif',
          fontWeight: 700,
          letterSpacing: '-0.04em'
        }}
      >
        $
      </span>
      <span className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.1em] opacity-75 [@media(max-height:740px)]:text-[8px]">
        cashback
      </span>
    </div>
  );
}

/** Cabeçalho de passo compacto (design V6): badge 22px + título 15px
 *  (+ dica opcional na mesma linha, pra não gastar altura). */
function StepHeader({
  n,
  title,
  hint
}: {
  n: number;
  title: string;
  hint?: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full bg-bow-royal text-[11px] font-semibold text-bow-cream"
        style={{ fontFamily: 'Chillax, system-ui, sans-serif' }}
      >
        {n}
      </span>
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
        <span
          className="text-bow-royal"
          style={{
            fontFamily: 'Chillax, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 15,
            letterSpacing: '-0.01em'
          }}
        >
          {title}
        </span>
        {hint && <span className="text-[11px] text-bow-royal/55">{hint}</span>}
      </div>
    </div>
  );
}

/** Destino do CTA principal do card Bow por plataforma (2026-07: o botão
 *  passou a empurrar o app nativo — paridade com os tiles dos parceiros). */
type BowCtaTarget =
  | 'native_in_app' // dentro do shell Capacitor: navegação interna de sempre
  | 'intent_android' // Android Chromium: intent:// (app se instalado; senão Play)
  | 'store_play' // Android sem intent:// confiável (Firefox…): Play direto
  | 'store_apple' // iOS: App Store direto (scheme-attempt no flip pós-1.2)
  | 'pwa_desktop'; // desktop/desconhecido: fluxo PWA atual

function resolveBowCta(): BowCtaTarget {
  if (isNativeApp()) return 'native_in_app';
  if (isApplePlatform()) return 'store_apple';
  if (isAndroidChromium()) return 'intent_android';
  if (isAndroidPlatform()) return 'store_play';
  return 'pwa_desktop';
}

/** Escolhe a loja certa pro aparelho. No iPhone/iPad isso evita mandar o
 *  usuário pro Google Play (que abre página de erro no Safari, não o app).
 *  Só cai pra outra loja se a apropriada não estiver cadastrada. */
function storeUrlForApp(app: ExternalAppRef): string | undefined {
  const apple = isApplePlatform();
  const primary = apple ? app.app_store_url : app.play_store_url;
  const fallback = apple ? app.play_store_url : app.app_store_url;
  return primary ?? fallback ?? undefined;
}

/** Resolve o destino do app pro conector escolhido, pulando a loja quando há
 *  deep link: por-conector (Spott) > por-carregador (Tupi/Move) > loja (BYD,
 *  platform-aware). O deep link universal abre o app direto no carregador e
 *  cai na loja se não instalado. */
function resolveAppUrl(
  app: ExternalAppRef,
  connector: number | null
): string | undefined {
  const perConnector =
    connector != null ? app.connector_deep_links?.[String(connector)] : undefined;
  if (perConnector) return perConnector;
  if (app.deep_link_url) return app.deep_link_url;
  return storeUrlForApp(app);
}

/** Tile de app de roaming (Tupi, Spott…) — quadrado pequeno do rodapé (design
 *  V6): logo + tarifa embaixo quando cadastrada no admin (operator_tariffs).
 *  Toca → abre o app no carregador (deep link) ou a loja, + analytics. */
function PartnerTile({
  app,
  connector,
  onClick
}: {
  app: ExternalAppRef;
  connector: number | null;
  onClick: (app: ExternalAppRef) => void;
}) {
  const storeUrl = resolveAppUrl(app, connector);
  const inner = (
    <>
      {app.icon_url ? (
        <img
          src={app.icon_url}
          alt={app.name}
          className="h-9 w-9 flex-shrink-0 rounded-xl object-contain"
        />
      ) : (
        <span
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-bow-royal/[0.06] text-base font-semibold text-bow-royal"
          aria-hidden
        >
          {app.name.charAt(0).toUpperCase()}
        </span>
      )}
      {app.tariff_per_kwh_brl && (
        <span className="text-[12px] font-semibold tracking-tight text-bow-royal">
          {formatBRL(app.tariff_per_kwh_brl)}
        </span>
      )}
    </>
  );
  const tileClass =
    'flex flex-1 flex-col items-center justify-center gap-1 rounded-[14px] bg-white px-1 pb-2 pt-2.5 shadow-soft [@media(max-height:740px)]:pb-1.5 [@media(max-height:740px)]:pt-2';

  if (!storeUrl) {
    return (
      <div className={tileClass} aria-label={app.name}>
        {inner}
      </div>
    );
  }
  return (
    <a
      href={storeUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => onClick(app)}
      aria-label={app.name}
      className={`${tileClass} no-underline transition active:scale-[0.98]`}
    >
      {inner}
    </a>
  );
}

function LoadingScreen() {
  return (
    <div className="mx-auto flex min-h-screen max-w-[420px] flex-col items-center justify-center gap-4 bg-bow-cream px-4">
      <img src={BOW_LOGO_SRC} alt="Bow" className="h-16 w-16 animate-pulse" />
      <p className="text-sm text-bow-royal/65">Carregando carregador…</p>
    </div>
  );
}

function ErrorScreen({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="mx-auto flex min-h-screen max-w-[420px] flex-col gap-5 bg-bow-cream px-5 py-10">
      <div className="flex items-center gap-2.5">
        <img src={BOW_LOGO_SRC} alt="Bow" className="h-7 w-7" />
        <span
          style={{
            fontFamily: 'Chillax, system-ui, sans-serif',
            fontWeight: 600,
            fontSize: 21,
            letterSpacing: '-0.04em'
          }}
        >
          bow
        </span>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
        <Display size={26} as="h1">
          Carregador não reconhecido
        </Display>
        <p className="max-w-[320px] text-sm leading-relaxed text-bow-royal/65">
          {message}. Verifique o adesivo no carregador ou fale com o suporte.
        </p>
        <div className="mt-3 flex w-full max-w-[280px] flex-col gap-2">
          <a
            href={SUPPORT_WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="no-underline"
          >
            <Button variant="primary" size="md" fullWidth type="button">
              Falar no WhatsApp
            </Button>
          </a>
          <Button variant="ghost" size="md" fullWidth onClick={onRetry}>
            Tentar de novo
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Pix — diamante rotacionado simplificando o mark (plano B do oficial Bacen). */
function PixGlyph({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <rect
        x="5"
        y="5"
        width="14"
        height="14"
        rx="3"
        transform="rotate(45 12 12)"
        fill="currentColor"
      />
    </svg>
  );
}

function WhatsAppGlyph() {
  return (
    <span
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
      style={{ background: 'rgba(37,211,102,0.12)' }}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="#25D366" aria-hidden>
        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm5.8 14.2c-.25.69-1.44 1.32-1.98 1.36-.53.04-1.02.23-3.45-.72-2.91-1.15-4.76-4.15-4.9-4.34-.14-.19-1.18-1.57-1.18-2.99 0-1.42.74-2.12 1.01-2.41.27-.29.58-.36.77-.36.19 0 .39 0 .56.01.18.01.42-.07.66.5.25.59.84 2.04.91 2.19.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.57.16.28.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.22 1.37.28.14.44.12.6-.07.16-.19.69-.81.88-1.08.18-.28.37-.23.62-.14.25.09 1.6.75 1.87.89.28.14.46.21.53.32.07.12.07.66-.18 1.35Z" />
      </svg>
    </span>
  );
}

function Arrow({ size = 18 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/** "Tarifa exclusiva" — etiqueta de preço (mesmo traço do V1 Canon). */
function TagGlyph({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12.6 2.6a2 2 0 0 0-1.4-.6H4a2 2 0 0 0-2 2v7.2a2 2 0 0 0 .6 1.4l8.2 8.2a2.4 2.4 0 0 0 3.4 0l6.2-6.2a2.4 2.4 0 0 0 0-3.4Z" />
      <circle cx="7.5" cy="7.5" r="1.3" fill="currentColor" stroke="none" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-bow-royal/40"
      aria-hidden
    >
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
